import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ICompentAiChatSettings } from '../shared/interface/site-settings';

interface ICopilotAction {
  type: string;
  pageKey?: string;
  entityRef?: string;
}

interface ICopilotResolveResponse {
  ok: boolean;
  route: string;
}

const CONTENT_PAGE_KEY = 'content-page';
const HOME_PAGE_KEY = 'home';

@Injectable({
  providedIn: 'root'
})
export class CompentChatService {
  private settings: ICompentAiChatSettings;

  constructor(
    private http: HttpClient,
    private router: Router,
    private zone: NgZone
  ) { }

  init(settings: ICompentAiChatSettings)
  {
    if (!settings?.loaderUrl || !settings.siteId || !settings.sessionUrl || this.isGuidePdfPage() || document.querySelector('script[data-site-id]')) {
      return;
    }

    this.settings = settings;

    const script = document.createElement('script');
    script.async = true;
    script.src = settings.loaderUrl;
    script.dataset.siteId = settings.siteId;
    script.dataset.channel = 'copilot';
    script.dataset.mode = 'popup';
    script.dataset.sessionUrl = settings.sessionUrl;
    script.onload = () => {
      document.body.classList.add('compent-chat');
      this.registerBridge();
    };
    document.body.appendChild(script);
  }

  private registerBridge()
  {
    const compentChat = (window as any).CompentChat;

    if (typeof compentChat !== 'function') {
      return;
    }

    compentChat('copilot', {
      getContext: () => this.getContext(),
      onAction: (action: ICopilotAction) => this.onAction(action),
      onNavigate: (url: string) => this.navigate(url)
    });
  }

  private getContext()
  {
    const pageId = this.currentPageId();

    return {
      pageKey: pageId ? CONTENT_PAGE_KEY : HOME_PAGE_KEY,
      entityRef: pageId ?? null,
      locale: document.documentElement.lang || 'da'
    };
  }

  private async onAction(action: ICopilotAction): Promise<{ ok: boolean; message?: string }>
  {
    if (action?.type !== 'open-page' || !action.pageKey) {
      return { ok: false, message: 'This assistant can only open pages.' };
    }

    let resolved: ICopilotResolveResponse;

    try {
      resolved = await firstValueFrom(this.http.get<ICopilotResolveResponse>(this.settings.resolveUrl, {
        params: { pageKey: action.pageKey, entityRef: action.entityRef ?? '' }
      }));
    } catch {
      return { ok: false, message: 'The page could not be found.' };
    }

    if (!resolved?.ok || !resolved.route) {
      return { ok: false, message: 'The page could not be found.' };
    }

    if (this.samePath(resolved.route, this.router.url)) {
      return { ok: true };
    }

    const navigated = await this.zone.run(() => this.router.navigateByUrl(resolved.route));

    return navigated ? { ok: true } : { ok: false, message: 'Navigation was blocked.' };
  }

  private navigate(url: string): boolean
  {
    let target: URL;

    try {
      target = new URL(url, location.href);
    } catch {
      return false;
    }

    if (target.origin !== location.origin) {
      return false;
    }

    this.zone.run(() => this.router.navigateByUrl(target.pathname + target.search + target.hash));

    return true;
  }

  private currentPageId(): string
  {
    let route = this.router.routerState.snapshot.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    const id = route.data?.['data']?.id;

    return id ? String(id) : null;
  }

  private samePath(first: string, second: string): boolean
  {
    const normalize = (value: string) => value.split(/[?#]/)[0].replace(/\/$/, '').toLowerCase() || '/';

    return normalize(first) === normalize(second);
  }

  private isGuidePdfPage()
  {
    return location.pathname.replace(/^\//, '').startsWith('guide-pdf');
  }
}
